const inputCls =
  "w-full border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:border-brand transition-colors duration-300 bg-white";
const codeCls = `${inputCls} font-mono text-xs`;

function Field({ label, hint, children }) {
  return (
    <label className="block mb-5">
      <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-slate-500 block mb-2">{label}</span>
      {children}
      {hint && <span className="block mt-2 text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

function SectionTitle({ children }) {
  return <h3 className="font-heading text-lg uppercase text-slate-900 mt-10 mb-5 pb-2 border-b border-slate-200">{children}</h3>;
}

const PAGES = [
  { key: "home", label: "Home page (/)" },
  { key: "contact", label: "Contact page (/contact)" },
  { key: "events", label: "Events page (/events)" },
];

export function AdminSeo({ seo, setSeo, setSeoPage }) {
  const count = (s) => (s || "").length;
  return (
    <div data-testid="admin-panel-seo">
      <h2 className="font-heading text-2xl uppercase text-slate-900 mb-2">SEO & Scripts</h2>
      <p className="text-sm text-slate-500 mb-8">Search engine settings, Google tools, and custom code. Changes go live after you press Save.</p>

      <SectionTitle>Site identity</SectionTitle>
      <Field label="Site name (browser tab & social shares)">
        <input className={inputCls} value={seo.site_name} onChange={(e) => setSeo("site_name", e.target.value)} data-testid="admin-seo-site-name-input" />
      </Field>
      <Field label="Public website address" hint="Used for canonical links, the sitemap and social share previews. Include https://">
        <input className={inputCls} value={seo.site_url} onChange={(e) => setSeo("site_url", e.target.value)} data-testid="admin-seo-site-url-input" />
      </Field>
      <Field label={`Default page title (${count(seo.default_title)}/60 recommended)`}>
        <input className={inputCls} value={seo.default_title} onChange={(e) => setSeo("default_title", e.target.value)} data-testid="admin-seo-default-title-input" />
      </Field>
      <Field label="Title suffix for inner pages" hint='Appended to inner page titles, e.g. "Contact Us | Drywall Local 675"'>
        <input className={inputCls} value={seo.title_suffix} onChange={(e) => setSeo("title_suffix", e.target.value)} data-testid="admin-seo-title-suffix-input" />
      </Field>
      <Field label={`Default meta description (${count(seo.default_description)}/160 recommended)`}>
        <textarea rows={3} className={inputCls} value={seo.default_description} onChange={(e) => setSeo("default_description", e.target.value)} data-testid="admin-seo-default-description-input" />
      </Field>
      <Field label="Keywords (comma separated)">
        <input className={inputCls} value={seo.keywords} onChange={(e) => setSeo("keywords", e.target.value)} data-testid="admin-seo-keywords-input" />
      </Field>
      <Field label="Social share image URL" hint="Shown when the site is shared on Facebook, LinkedIn, iMessage, etc. 1200×630 works best. Leave as /og-image.jpg for the default seal image.">
        <input className={inputCls} value={seo.og_image} onChange={(e) => setSeo("og_image", e.target.value)} data-testid="admin-seo-og-image-input" />
      </Field>
      <label className="flex items-center gap-3 mb-5 cursor-pointer">
        <input type="checkbox" checked={seo.robots_index !== false} onChange={(e) => setSeo("robots_index", e.target.checked)} className="h-4 w-4 accent-brand" data-testid="admin-seo-robots-index-checkbox" />
        <span className="text-sm text-slate-700 font-semibold">Allow search engines to index this site</span>
      </label>

      <SectionTitle>Per-page titles & descriptions</SectionTitle>
      {PAGES.map((p) => (
        <div key={p.key} className="border border-slate-200 p-5 mb-4" data-testid={`admin-seo-page-${p.key}`}>
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-brand mb-4">{p.label}</p>
          <Field label={`Title (${count(seo.pages?.[p.key]?.title)}/60)`}>
            <input className={inputCls} value={seo.pages?.[p.key]?.title || ""} onChange={(e) => setSeoPage(p.key, "title", e.target.value)} data-testid={`admin-seo-page-${p.key}-title-input`} />
          </Field>
          <Field label={`Description (${count(seo.pages?.[p.key]?.description)}/160)`}>
            <textarea rows={2} className={inputCls} value={seo.pages?.[p.key]?.description || ""} onChange={(e) => setSeoPage(p.key, "description", e.target.value)} data-testid={`admin-seo-page-${p.key}-description-input`} />
          </Field>
        </div>
      ))}

      <SectionTitle>Google & search tools</SectionTitle>
      <Field label="Google Analytics 4 measurement ID" hint="Looks like G-XXXXXXXXXX. Find it in Google Analytics → Admin → Data streams.">
        <input className={inputCls} placeholder="G-XXXXXXXXXX" value={seo.google_analytics_id} onChange={(e) => setSeo("google_analytics_id", e.target.value.trim())} data-testid="admin-seo-ga-input" />
      </Field>
      <Field label="Google Tag Manager container ID" hint="Looks like GTM-XXXXXXX. Optional — only if you use Tag Manager.">
        <input className={inputCls} placeholder="GTM-XXXXXXX" value={seo.google_tag_manager_id} onChange={(e) => setSeo("google_tag_manager_id", e.target.value.trim())} data-testid="admin-seo-gtm-input" />
      </Field>
      <Field label="Google Search Console verification code" hint='Paste only the content value from the tag Google gives you: <meta name="google-site-verification" content="THIS_PART" />'>
        <input className={inputCls} value={seo.google_site_verification} onChange={(e) => setSeo("google_site_verification", e.target.value.trim())} data-testid="admin-seo-gsc-input" />
      </Field>
      <Field label="Bing Webmaster verification code" hint="Optional — the content value from the msvalidate.01 meta tag.">
        <input className={inputCls} value={seo.bing_site_verification} onChange={(e) => setSeo("bing_site_verification", e.target.value.trim())} data-testid="admin-seo-bing-input" />
      </Field>

      <SectionTitle>Custom scripts & code</SectionTitle>
      <p className="text-xs text-slate-400 mb-5">Paste full HTML snippets including the &lt;script&gt; tags. They run on every public page (not in admin). Only paste code from services you trust.</p>
      <Field label="Header — inside <head>" hint="Meta tags, verification tags, analytics snippets, fonts.">
        <textarea rows={5} className={codeCls} spellCheck={false} value={seo.head_html} onChange={(e) => setSeo("head_html", e.target.value)} data-testid="admin-seo-head-html-input" />
      </Field>
      <Field label="Body — right after <body> opens" hint="Tag Manager noscript fallbacks, chat widgets.">
        <textarea rows={5} className={codeCls} spellCheck={false} value={seo.body_start_html} onChange={(e) => setSeo("body_start_html", e.target.value)} data-testid="admin-seo-body-html-input" />
      </Field>
      <Field label="Footer — just before </body> closes" hint="Chat widgets, pixels, anything that should load last.">
        <textarea rows={5} className={codeCls} spellCheck={false} value={seo.footer_html} onChange={(e) => setSeo("footer_html", e.target.value)} data-testid="admin-seo-footer-html-input" />
      </Field>

      <SectionTitle>Automatic SEO already in place</SectionTitle>
      <ul className="text-sm text-slate-600 list-disc pl-5 space-y-1.5" data-testid="admin-seo-auto-list">
        <li>Canonical URLs, Open Graph and Twitter share tags on every page</li>
        <li>Organization / LocalBusiness structured data (address, phone, hours) and Event structured data on the Events page</li>
        <li>
          XML sitemap at <code className="font-mono text-xs bg-slate-100 px-1">/api/sitemap.xml</code> and robots.txt (admin pages excluded)
        </li>
        <li>Favicon, Apple touch icon and web-app manifest with the Local 675 seal</li>
      </ul>
    </div>
  );
}
